"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import {
	isEngineWizard,
	outputPathFromResult,
	runEngineWizard,
	runWizard,
	wizardOutputUrl,
} from "@/lib/ai-wizard";
import { helperRunEngineWizard } from "@/lib/helper-engines";
import { ComboRecipes } from "./ComboRecipes";
import { pushRecent, RecentUsed } from "./RecentUsed";
import { WizardGrid } from "./WizardGrid";
import {
	COMBOS,
	type ComboRecipe,
	RUN_ALL_COIN_COST,
	type Wizard,
	WIZARDS,
} from "./wizards-data";
import styles from "./MagicWandModal.module.css";

/**
 * MagicWandModal — Phase 25-AB Task 14.
 *
 * Mockup source: docs/mockups/magic-wand.html (panels 26 — 29).
 *
 * A full-screen overlay (portal → <body>) housing:
 *   - Hero header: "AI Magic Wand" title + balance pill + Run All CTA
 *   - Filter row: search input + filter pills (모두 / 무료 / +1코인 / 신규)
 *   - WizardGrid: 50 wizards in 9 categories (filtered)
 *   - Right rail: ComboRecipes + RecentUsed
 *
 * ESC and backdrop click both close. The Run All button fires
 * `onRunAll` if provided, otherwise just logs. Wizard execution is
 * delegated upward through `onRunWizard` so the parent can wire it to
 * the real pipeline — the modal only handles recent-used localStorage
 * persistence.
 */

export type FilterPill = "all" | "free" | "low" | "new";

/**
 * Editor media the engine wizards operate on. Supplied by EditorHeader25
 * from the current project's media assets. `video` is the main clip,
 * `audio` the main audio track, `bgm` an optional second audio track used
 * by the BGM-ducking wizard (AI-18).
 */
export interface WizardMedia {
	video?: File;
	audio?: File;
	bgm?: File;
}

export interface MagicWandModalProps {
	open: boolean;
	onClose: () => void;
	onRunWizard?: (wizard: Wizard) => void;
	onRunCombo?: (combo: ComboRecipe) => void;
	onRunAll?: () => void;
	balance?: number;
	/** Returns the editor's current media for engine wizards. */
	getMedia?: () => WizardMedia;
}

const FILTER_PILLS: { key: FilterPill; label: string }[] = [
	{ key: "all", label: "모두" },
	{ key: "free", label: "무료만" },
	{ key: "low", label: "+1코인 이하" },
	{ key: "new", label: "신규" },
];

function filterPredicate(pill: FilterPill): (w: Wizard) => boolean {
	switch (pill) {
		case "free":
			return (w) => w.coinCost === 0;
		case "low":
			return (w) => w.coinCost <= 1;
		case "new":
			return (w) => !!w.isNew;
		default:
			return () => true;
	}
}

export function MagicWandModal({
	open,
	onClose,
	onRunWizard,
	onRunCombo,
	onRunAll,
	balance = 273,
	getMedia,
}: MagicWandModalProps) {
	const router = useRouter();
	const [mounted, setMounted] = useState(false);
	const [query, setQuery] = useState("");
	const [filter, setFilter] = useState<FilterPill>("all");
	// Bump this every time we push to recent so RecentUsed re-reads.
	const [recentVersion, setRecentVersion] = useState(0);
	// Increment 6/11 — execution feedback. Increment 11 wires the wizards that
	// have a live backend endpoint (Worker /ai/* → FastAPI core → Claude).
	// Phase 25 engine phase — engine wizards additionally upload the editor's
	// current media and surface a download link for the processed output.
	const [runStatus, setRunStatus] = useState<string | null>(null);
	const [running, setRunning] = useState(false);
	const [outputUrl, setOutputUrl] = useState<string | null>(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (!open) return;
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") onClose();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	const handleRunWizard = useCallback(
		async (key: string) => {
			const w = WIZARDS.find((x) => x.key === key);
			if (!w || running) return;
			pushRecent(key);
			setRecentVersion((v) => v + 1);
			onRunWizard?.(w);
			setRunning(true);
			setOutputUrl(null);
			setRunStatus(`"${w.name}" 실행 중…`);

			let res: Awaited<ReturnType<typeof runWizard>>;
			if (isEngineWizard(key)) {
				// Engine wizard — try the local Helper first (hybrid architecture:
				// the user's machine has the file + ffmpeg + Python + GPU). Fall
				// back to the server engine path only when Helper is offline.
				const media = getMedia?.() ?? {};
				const isAudioWizard = key === "AI-33" || key === "AI-36";
				const primary = isAudioWizard
					? (media.audio ?? media.video)
					: (media.video ?? media.audio);
				if (!primary) {
					setRunning(false);
					setRunStatus(
						`"${w.name}" — 편집기에 영상/오디오가 없습니다. 먼저 미디어를 추가하세요.`,
					);
					return;
				}
				setRunStatus(`"${w.name}" — 로컬 Helper 처리 중…`);
				const helperRes = await helperRunEngineWizard({
					wizardKey: key,
					file: primary,
					bgmFile: media.bgm,
				}, (pct) => setRunStatus(`"${w.name}" — 로컬 Helper 처리 중… ${pct}%`));
				if (helperRes.ok) {
					setRunning(false);
					if (helperRes.downloadUrl) setOutputUrl(helperRes.downloadUrl);
					setRunStatus(
						`"${w.name}" 완료 — 로컬 (${helperRes.engine ?? "helper"}) 처리. 다운로드 준비됨.`,
					);
					return;
				}
				// "Helper cannot do this" codes -> fall back to server engine.
				// Real processing failures surface so the user sees them.
				const fallbackErrors = new Set([
					"helper_unavailable",
					"helper_no_workspace",
					"helper_wizard_not_supported",
					"PYTHON_SCRIPT_NOT_ALLOWED",
					"PYTHON_NOT_FOUND",
					"FFMPEG_NOT_FOUND",
				]);
				const errCode = helperRes.error ?? "";
				if (
					fallbackErrors.has(errCode) ||
					fallbackErrors.has(errCode.toUpperCase()) ||
					errCode.includes("PYTHON_SCRIPT_NOT_ALLOWED")
				) {
					setRunStatus(
						`"${w.name}" — Helper로 처리 불가 (${errCode || "미설치"}). 서버에서 처리 중…`,
					);
					res = await runEngineWizard(key, primary, { bgmFile: media.bgm });
				} else {
					setRunning(false);
					setRunStatus(
						`"${w.name}" — Helper 실패: ${errCode || "알 수 없는 오류"}`,
					);
					return;
				}
			} else {
				res = await runWizard(key);
			}

			setRunning(false);
			if (res.ok) {
				const outPath = outputPathFromResult(res);
				if (outPath) {
					setOutputUrl(wizardOutputUrl(outPath));
					setRunStatus(
						`"${w.name}" 완료 — 처리된 파일이 준비됐습니다. 아래에서 내려받으세요.`,
					);
				} else {
					setRunStatus(
						`"${w.name}" 완료 — 백엔드(${res.endpoint})에서 결과를 받았습니다.`,
					);
				}
				return;
			}
			if (res.status === 401) {
				router.push("/login");
				return;
			}
			if (res.error === "not_implemented") {
				setRunStatus(
					`"${w.name}" — 이 마법사의 백엔드 엔드포인트는 아직 없습니다 (다음 단계 예정).`,
				);
				return;
			}
			if (res.error === "insufficient_credits") {
				setRunStatus(
					`"${w.name}" — 코인 부족 (보유 ${res.balance ?? 0} · 필요 ${res.required ?? "?"}).`,
				);
				return;
			}
			if (res.error === "bgm_track_required") {
				setRunStatus(
					`"${w.name}" — BGM 트랙이 필요합니다. 편집기에 BGM 오디오를 추가한 뒤 다시 실행하세요.`,
				);
				return;
			}
			setRunStatus(
				`"${w.name}" 실행 실패: ${res.error ?? "알 수 없는 오류"}`,
			);
		},
		[getMedia, onRunWizard, running, router],
	);

	/**
	 * Run a chain of wizards sequentially. Engine wizards (the 8 ffmpeg/ML
	 * ones) need the editor's current media; non-engine wizards are run with
	 * an empty payload. Aggregates progress + per-step result so the user
	 * sees live status, the last engine output offers a download link.
	 */
	const runSequence = useCallback(
		async (keys: readonly string[], label: string) => {
			const media = getMedia?.() ?? {};
			const total = keys.length;
			let lastDownloadUrl: string | null = null;
			const failed: { key: string; name: string; error: string }[] = [];
			let success = 0;
			let skipped = 0;
			for (let i = 0; i < total; i += 1) {
				const key = keys[i]!;
				const w = WIZARDS.find((x) => x.key === key);
				if (!w) continue;
				pushRecent(key);
				setRunStatus(`${label} — ${i + 1}/${total} 실행 중: "${w.name}"`);
				let res: Awaited<ReturnType<typeof runWizard>>;
				if (isEngineWizard(key)) {
					const isAudioWizard = key === "AI-33" || key === "AI-36";
					const primary = isAudioWizard
						? (media.audio ?? media.video)
						: (media.video ?? media.audio);
					if (!primary) {
						skipped += 1;
						failed.push({
							key,
							name: w.name,
							error: "no_media",
						});
						continue;
					}
					// Hybrid: local Helper first, server only when offline.
					const helperRes = await helperRunEngineWizard({
						wizardKey: key,
						file: primary,
						bgmFile: media.bgm,
					});
					if (helperRes.ok) {
						success += 1;
						if (helperRes.downloadUrl) lastDownloadUrl = helperRes.downloadUrl;
						continue;
					}
					const seqFallbackErrors = new Set([
						"helper_unavailable",
						"helper_no_workspace",
						"helper_wizard_not_supported",
						"PYTHON_SCRIPT_NOT_ALLOWED",
						"PYTHON_NOT_FOUND",
						"FFMPEG_NOT_FOUND",
					]);
					const seqErrCode = helperRes.error ?? "";
					if (
						!seqFallbackErrors.has(seqErrCode) &&
						!seqFallbackErrors.has(seqErrCode.toUpperCase()) &&
						!seqErrCode.includes("PYTHON_SCRIPT_NOT_ALLOWED")
					) {
						failed.push({ key, name: w.name, error: seqErrCode || "helper_failed" });
						continue;
					}
					res = await runEngineWizard(key, primary, { bgmFile: media.bgm });
				} else {
					res = await runWizard(key);
				}
				if (res.ok) {
					success += 1;
					const out = outputPathFromResult(res);
					if (out) lastDownloadUrl = wizardOutputUrl(out);
				} else if (res.status === 401) {
					setRunStatus(`${label} — 인증이 필요합니다. 로그인 페이지로 이동합니다.`);
					router.push("/login");
					return;
				} else {
					failed.push({ key, name: w.name, error: res.error ?? "unknown" });
				}
			}
			setRecentVersion((v) => v + 1);
			if (lastDownloadUrl) setOutputUrl(lastDownloadUrl);
			const parts = [
				`${label} 완료 — 성공 ${success}/${total}`,
				skipped > 0 ? `미디어 없어 건너뜀 ${skipped}` : "",
				failed.length > 0
					? `실패 ${failed.length} (${failed
							.map((f) => `${f.name}:${f.error}`)
							.slice(0, 3)
							.join(" / ")}${failed.length > 3 ? " …" : ""})`
					: "",
			].filter(Boolean);
			setRunStatus(parts.join(" · "));
		},
		[getMedia, router],
	);

	const handleRunCombo = useCallback(
		async (combo: ComboRecipe) => {
			if (running) return;
			onRunCombo?.(combo);
			setRunning(true);
			setOutputUrl(null);
			await runSequence(combo.wizards, `콤보 "${combo.name}"`);
			setRunning(false);
		},
		[onRunCombo, runSequence, running],
	);

	const handleRunAll = useCallback(async () => {
		if (running) return;
		onRunAll?.();
		// Run All executes the "perfect" combo (the canonical full chain).
		const perfect = COMBOS.find((c) => c.key === "perfect") ?? COMBOS[0];
		setRunning(true);
		setOutputUrl(null);
		await runSequence(perfect.wizards, `Run All (${RUN_ALL_COIN_COST} 코인)`);
		setRunning(false);
	}, [onRunAll, runSequence, running]);

	const filterFn = useMemo(() => filterPredicate(filter), [filter]);

	if (!open || !mounted) return null;

	return createPortal(
		<div
			className={styles.mwBackdrop}
			role="presentation"
			onClick={onClose}
			data-testid="magic-wand-backdrop"
		>
			<div
				className={styles.mw}
				role="dialog"
				aria-modal="true"
				aria-label="AI Magic Wand"
				data-testid="magic-wand-modal"
				onClick={(e) => e.stopPropagation()}
				{...devLabelPanel("26")}
			>
				<header className={styles.mwHeader} {...devLabel("26-A")}>
					<div className={styles.hTitle}>
						AI Magic Wand{" "}
						<span className={styles.mmlTag}>MML+</span>
					</div>
					<div className={styles.hSpacer} />
					<span
						className={styles.hBalance}
						data-testid="magic-wand-balance"
					>
						잔여 {balance}
					</span>
					<button
						type="button"
						className={styles.hRunAll}
						onClick={handleRunAll}
						data-testid="magic-wand-run-all"
						aria-label={`전체 실행 (${RUN_ALL_COIN_COST} 코인)`}
					>
						▶ Run All ({RUN_ALL_COIN_COST} 코인)
					</button>
					<button
						type="button"
						className={styles.hClose}
						onClick={onClose}
						aria-label="닫기"
						data-testid="magic-wand-close"
					>
						×
					</button>
				</header>

				{runStatus && (
					<div
						data-testid="magic-wand-run-status"
						role="status"
						style={{
							margin: "0 16px 8px",
							padding: "8px 12px",
							borderRadius: 6,
							border: "1px solid #d4af37",
							background: "rgba(212,175,55,0.08)",
							color: "#d4af37",
							fontSize: 11,
							lineHeight: 1.5,
						}}
					>
						<span
							onClick={() => {
								setRunStatus(null);
								setOutputUrl(null);
							}}
							style={{ cursor: "pointer" }}
						>
							{runStatus}
						</span>
						{outputUrl && (
							<>
								{"  "}
								<a
									href={outputUrl}
									download
									data-testid="magic-wand-output-download"
									style={{
										color: "#d4af37",
										textDecoration: "underline",
										fontWeight: 600,
									}}
								>
									처리된 파일 내려받기 ↓
								</a>
							</>
						)}
					</div>
				)}

				<div className={styles.mwBody}>
					<section
						className={styles.mwCenter}
						{...devLabelPanel("28")}
					>
						<div
							className={styles.filterRow}
							{...devLabel("28-A")}
						>
							<input
								type="text"
								className={styles.filterSearch}
								placeholder="기능 검색 (예: 자막, 썸네일, 트렌드...)"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								data-testid="magic-wand-search"
							/>
						</div>
						<div className={styles.filterPills}>
							{FILTER_PILLS.map((p) => (
								<button
									key={p.key}
									type="button"
									className={`${styles.filterPill} ${filter === p.key ? styles.on : ""}`}
									onClick={() => setFilter(p.key)}
									data-testid={`filter-pill-${p.key}`}
									aria-pressed={filter === p.key}
								>
									{p.label}
								</button>
							))}
						</div>

						<WizardGrid
							onRunWizard={handleRunWizard}
							query={query}
							filter={filterFn}
						/>
					</section>

					<aside
						className={styles.mwRight}
						{...devLabelPanel("29")}
					>
						<RecentUsed
							onRunWizard={handleRunWizard}
							version={recentVersion}
						/>
						<ComboRecipes onRunCombo={handleRunCombo} />
					</aside>
				</div>
			</div>
		</div>,
		document.body,
	);
}
